import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/base/SEO';
import { testimonials, features } from '@/mocks/asmr';

export default function AboutPage() {
  const pageTitle = '关于我们 - ASMR免费网页';
  const pageDesc = 'ASMR免费网页致力于提供海量免费ASMR音频在线收听服务，无需下载、无需注册。了解我们的品牌故事、用户口碑与联系方式。';
  const pageUrl = 'https://asmr-free.com/about';

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: pageTitle,
      description: pageDesc,
      url: pageUrl,
      inLanguage: 'zh-CN',
      about: {
        '@type': 'Organization',
        name: 'ASMR免费网页',
        description: 'ASMR免费网页，致力于提供高质量免费ASMR在线收听服务。',
        foundingDate: '2023',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://asmr-free.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: pageUrl },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords="ASMR免费网页,关于ASMR免费网页,ASMR网站介绍,免费ASMR平台"
        canonicalUrl={pageUrl}
        schemaData={pageSchema}
      />
      <Navbar />
      <main>
        <section className="relative pt-16">
          <div className="relative h-56 md:h-72 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20cozy%20home%20office%20with%20headphones%20on%20wooden%20desk%2C%20soft%20afternoon%20sunlight%2C%20peaceful%20creative%20workspace%2C%20minimalist%20interior%20design%2C%20cream%20and%20warm%20amber%20tones%2C%20editorial%20photography&width=1920&height=600&seq=about-header&orientation=landscape"
              alt="关于ASMR免费网页"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-4">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-2">
                  关于我们
                </h1>
                <p className="text-background-50/70 text-sm md:text-base">
                  用声音传递温暖，让每一颗心灵都能找到安放之处
                </p>
              </div>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/60" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-700 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 text-center">
              品牌故事
            </h2>
            <div className="prose-custom space-y-5">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                <strong className="text-foreground-900">ASMR免费网页</strong>创立于2023年，怀着"让优质助眠音频触手可及"的初心，我们致力于打造一个真正免费、无门槛的ASMR在线收听平台。
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                在快节奏的现代生活中，失眠、焦虑、注意力不集中等问题困扰着越来越多的人。我们发现，高质量的ASMR音频能够有效缓解这些问题，但市面上大多数平台要么收费昂贵，要么需要繁琐的注册流程。于是，ASMR免费网页应运而生。
              </p>
              <p className="text-base text-foreground-600 leading-relaxed">
                我们坚信，放松和优质睡眠是每个人的基本权利，不应被金钱或技术门槛所阻隔。因此，我们坚持<strong className="text-foreground-900">完全免费、无需注册、无需下载</strong>的原则，让每一位用户都能轻松享受ASMR带来的身心益处。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12">
              {[
                { icon: 'ri-eye-line', title: '我们的愿景', text: '成为最受欢迎的中文ASMR在线平台，让千万用户享受高品质的免费助眠服务。' },
                { icon: 'ri-heart-line', title: '我们的使命', text: '用声音疗愈心灵，让每一位用户都能找到属于自己的放松方式。' },
                { icon: 'ri-shield-star-line', title: '我们的承诺', text: '永久免费、无需注册、持续更新、保护隐私，用户的信任是我们最珍视的财富。' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              用户口碑
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">来自真实用户的真挚反馈</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-2xl bg-background-50 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(t.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-500 text-sm text-center mb-10">
              如果您有任何建议、合作意向或问题反馈，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'support@asmr-free.com', href: 'mailto:support@asmr-free.com' },
                { icon: 'ri-wechat-line', title: '微信公众号', value: 'ASMR免费网页', href: '#' },
                { icon: 'ri-weibo-line', title: '官方微博', value: '@ASMR免费网页', href: '#' },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  className="group p-6 rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all duration-300 text-center cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-foreground-500">{item.value}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}